import { Badge } from "../../../design-v2";
import SectionBlock from "../SectionBlock";
import SelectableCard from "../SelectableCard";
import ChipSelector from "../ChipSelector";
import SegmentedSelector from "../SegmentedSelector";
import InlineStepper from "../InlineStepper";
import CollapsibleBlock from "../CollapsibleBlock";
import SummaryBadgeList from "../SummaryBadgeList";
import { findFieldError, fromTextList, toTextList } from "../settingsMappers";
import { ChevronDownGlyph } from "../settingsGlyphs";

export const INPUT_CLASSES = "lz-settings-input";

export const TEXTAREA_CLASSES = "lz-settings-textarea";

function updateNestedValue(target, path, value) {
  let current = target;

  for (let index = 0; index < path.length - 1; index += 1) {
    const key = path[index];

    if (!current[key] || typeof current[key] !== "object") {
      current[key] = {};
    }

    current = current[key];
  }

  current[path[path.length - 1]] = value;
}

export function applyDraftUpdate(currentDraft, onChange, producer) {
  const nextDraft = JSON.parse(JSON.stringify(currentDraft || {}));
  producer(nextDraft);
  onChange(nextDraft);
}

export function setDraftField(currentDraft, onChange, path, value) {
  applyDraftUpdate(currentDraft, onChange, (nextDraft) => {
    updateNestedValue(nextDraft, path, value);
  });
}

export function Field({ label, hint, error, children }) {
  return (
    <div className="min-w-0">
      {label ? <label className="lz-settings-label">{label}</label> : null}
      {hint ? <p className="lz-settings-hint">{hint}</p> : null}
      {children}
      {error ? <p className="lz-settings-error">{error}</p> : null}
    </div>
  );
}

export function CharacterCount({ current, limit }) {
  const isOverLimit = current > limit;

  return (
    <p
      className={[
        "lz-settings-count",
        isOverLimit ? "lz-settings-count--over" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {current}/{limit}
    </p>
  );
}

export function ReadonlyChipList({
  values,
  labelMap,
  emptyLabel = "None",
  tone = "neutral",
  tagLabel,
}) {
  if (!Array.isArray(values) || values.length === 0) {
    return <p className="lz-settings-hint">{emptyLabel}</p>;
  }

  return (
    <div className="lz-settings-chip-row mt-2">
      {values.map((value) => (
        <Badge key={value} tone={tone === "strong" ? "action" : "neutral"}>
          <span>{labelMap[value] || value}</span>
          {tagLabel ? <span className="lz-settings-badge-meta">{tagLabel}</span> : null}
        </Badge>
      ))}
    </div>
  );
}

export function TextListField({
  label,
  hint,
  error,
  value,
  onChange,
  placeholder,
}) {
  return (
    <Field label={label} hint={hint} error={error}>
      <textarea
        rows={4}
        className={`${TEXTAREA_CLASSES} mt-2`}
        value={toTextList(value)}
        onChange={(event) => onChange(fromTextList(event.target.value))}
        placeholder={placeholder}
      />
    </Field>
  );
}

export function SelectorTrigger({
  label,
  valueLabel,
  placeholder,
  onClick,
  error,
}) {
  return (
    <div className="min-w-0">
      <button type="button" onClick={onClick} className="lz-settings-trigger mt-2">
        <span className="min-w-0 flex-1">
          <span className="lz-settings-label">{label}</span>
          <span
            className={[
              "lz-settings-trigger__value block",
              valueLabel ? "" : "lz-settings-trigger__value--placeholder",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {valueLabel || placeholder}
          </span>
        </span>
        <ChevronDownGlyph className="lz-settings-row__chevron" />
      </button>
      {error ? <p className="lz-settings-error">{error}</p> : null}
    </div>
  );
}

export function formatTokenLabel(value) {
  return String(value || "")
    .replace(/^ex_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase())
    .trim();
}

export {
  ChipSelector,
  CollapsibleBlock,
  InlineStepper,
  SectionBlock,
  SegmentedSelector,
  SelectableCard,
  SummaryBadgeList,
  findFieldError,
};
